/* eslint-disable max-lines */

import {
  APUATValidationsResponse,
  ActionResponse,
  ProductsInterOpResponse,
  TargetVersionsResponse
} from '../../types/ApiResponseData';
import {
  APUAT_VALIDATIONS_URL,
  GET_AVAILABLE_UPGRADE_VERSIONS_URL,
  GET_PRODUCTS_INTEROP_MATRIX,
  GET_PRODUCT_VERSIONS_URL,
  GET_UPGRADE_LCM_REPOSITORY_URL,
  GET_UPGRADE_STATE_URL,
  RUN_PRECHECK_UPGRADE_URL,
  UPGRADE_PRODUCT_URL,
  GET_TARGET_VERSIONS_URL,
  FIND_UPGRADE_PATH_URL,
  RETRIEVE_VRA_UPGRADE_PLAN_URL,
  GET_ABXCLOUDPROXY_UPGRADE_VERSION_URL,
  GET_ABXCLOUD_PROXY_UPGRADE_VERSION_DETAILS_URL
} from '../../constants/ApiUri';
import { Http, getLaterVersions } from 'vrlcm-gui-utils';
import {
  ProductVersions,
  UpgradeState
} from '../../types/workflows/UpgradeWorkflow';

import { RequestId, UpgradePlan, UpgradePlanInput } from 'vrlcm-gui-types';
import { UpgradeVersionPayload } from '../../components/workflows/upgrade-workflow/steps/upgrade-product/core/types';
import { major } from '../../utils/UtilFunctions';
import { oc } from 'ts-optchain';

const VIDM_FIXED_UPGRADE_TARGET_VERSIONS = {
  '2.9.2': ['3.2.0'],
  '3.1.0': ['3.2.0'],
  '3.2.0': ['3.3.0'],
  '3.3.0': ['3.3.1'],
  '3.3.1': ['3.3.2'],
  '3.3.2': ['3.3.4', '3.3.5'],
  '3.3.3': ['3.3.4', '3.3.5', '3.3.6'],
  '3.3.4': ['3.3.5', '3.3.6'],
  '3.3.5': ['3.3.6']
};

const VRNI_FIXED_UPGRADE_TARGET_VERSIONS = {
  '4.0.0': ['4.2.0'],
  '4.1.0': ['4.2.0', '5.0.0'],
  '4.1.1': ['4.2.0', '5.0.0'],
  '4.2.0': ['5.0.0', '5.1.0'],
  '5.0.0': ['5.1.0', '5.2.0'],
  '5.1.0': ['5.2.0', '5.3.0'],
  '5.2.0': ['5.3.0', '6.0.0'],
  '5.3.0': ['6.0.0', '6.1.0'],
  '6.0.0': ['6.1.0', '6.2.0'],
  '6.1.0': ['6.2.0', '6.3.0'],
  '6.2.0': ['6.3.0', '6.4.0'],
  '6.3.0': ['6.4.0', '6.5.0', '6.5.1'],
  '6.4.0': ['6.5.0', '6.5.1', '6.6.0'],
  '6.5.0': ['6.5.1', '6.6.0'],
  '6.5.1': ['6.6.0', '6.7.0'],
  '6.6.0': ['6.7.0', '6.8.0'],
  '6.7.0': ['6.8.0', '6.9.0'],
  '6.8.0': ['6.9.0', '6.10.0'],
  '6.9.0': ['6.10.0', '6.11.0'],
  '6.10.0': ['6.11.0', '6.12.0', '6.12.1'],
  '6.11.0': ['6.12.0', '6.12.1','6.13.0'],
  '6.12.0': ['6.12.1','6.13.0'],
  '6.12.1': ['6.13.0']
};

export default class UpgradeWorkflowAPI {
  public static productVersions: ProductVersions[] = [];
  public static vcfProductVersions: ProductVersions[] = [];
  public static targetVersions: TargetVersionsResponse | null = null;

  public static getUpgradeState = async (
    requestId: string
  ): Promise<UpgradeState> => {
    const state = await Http.get(GET_UPGRADE_STATE_URL(requestId));
    return state.data;
  };

  public static getLcmRepositoryUrl = async (
    product: string,
    version: string
  ): Promise<string> => {
    const response = await Http.get(
      GET_UPGRADE_LCM_REPOSITORY_URL(product, version)
    );
    return oc(response).data.url();
  };

  public static checkProductCompatibilityInSDDCManager = (
    productId: string
  ) => {
    return !['vssc', 'vrni', 'vrbc', 'vro'].includes(productId);
  };

  public static getUpgradableProductVersions = async (
    productId: string,
    currentVersion: string | null,
    vcfEnabledEnvironment: boolean
  ): Promise<string[]> => {
    const productVersions =
      vcfEnabledEnvironment &&
      UpgradeWorkflowAPI.checkProductCompatibilityInSDDCManager(productId)
        ? 'vcfProductVersions'
        : 'productVersions';

    if (productId === 'vidm') {
      const targetVersions = getTargetVidmVersions(currentVersion);
      if (targetVersions.length > 0) {
        return targetVersions;
      }
    }

    if (productId === 'vrni') {
      const targetVersions = getTargetVrniVersions(currentVersion);
      if (targetVersions.length > 0) {
        return targetVersions;
      }
    }
    await UpgradeWorkflowAPI.fetchProductVersions(
      vcfEnabledEnvironment,
      productId
    );

    const product = UpgradeWorkflowAPI[productVersions].find(
      p => p.productId === productId
    );

    let upgradableVersions: string[] = currentVersion
      ? getLaterVersions(product ? product.versions : [], currentVersion)
      : [];

    if (productId === 'vra' && currentVersion) {
      if (major(currentVersion) <= 7) {
        upgradableVersions = upgradableVersions.filter(v => major(v) <= 7);
      } else {
        upgradableVersions = upgradableVersions.filter(v => major(v) >= 8);
      }
    }

    return upgradableVersions;
  };

  // For vRA and vRB only. This will give a requestId which we have to poll.
  public static getAvailableUpgradeVersions = async (
    payload: UpgradeVersionPayload
  ): Promise<string> => {
    const response = await Http.post(
      GET_AVAILABLE_UPGRADE_VERSIONS_URL,
      payload
    );
    return response.data.requestId;
  };

  public static abxCloudProxyGetUpgradeVersionRequest = async (
    environmentId: string
  ): Promise<RequestId> => {
    const response = await Http.get(
      GET_ABXCLOUDPROXY_UPGRADE_VERSION_URL(environmentId)
    );
    return response.data;
  };

  public static getabxCloudProxyUpgradeVersionDetails = async (
    requestId: string
  ) => {
    const response = await Http.get(
      GET_ABXCLOUD_PROXY_UPGRADE_VERSION_DETAILS_URL(requestId)
    );
    return response.data;
  };

  public static getProductVersions = async (
    productId: string,
    vcfEnabledEnvironment: boolean
  ): Promise<string[]> => {
    const productVersions =
      vcfEnabledEnvironment &&
      UpgradeWorkflowAPI.checkProductCompatibilityInSDDCManager(productId)
        ? 'vcfProductVersions'
        : 'productVersions';
    await UpgradeWorkflowAPI.fetchProductVersions(
      vcfEnabledEnvironment,
      productId
    );

    const product = UpgradeWorkflowAPI[productVersions].find(
      p => p.productId === productId
    );
    return product ? product.versions : [];
  };

  public static fetchProductVersions = async (
    vcfEnabledEnvironment: boolean,
    productId?: string
  ) => {
    const checkSDDCManager =
      vcfEnabledEnvironment &&
      UpgradeWorkflowAPI.checkProductCompatibilityInSDDCManager(
        productId || ''
      );
    const productVersions = checkSDDCManager
      ? 'vcfProductVersions'
      : 'productVersions';

    if (UpgradeWorkflowAPI[productVersions].length === 0) {
      UpgradeWorkflowAPI[productVersions] = await Http.get<ProductVersions[]>(
        `${GET_PRODUCT_VERSIONS_URL}?vcfEnabledEnvironment=${checkSDDCManager}&isUpgradeFlow=${vcfEnabledEnvironment}`
      ).then(res => res.data);
    }
  };

  public static patchUpgradeDetails = async (
    state: UpgradeState
  ): Promise<void> => {
    const response = await Http.post(UPGRADE_PRODUCT_URL, state);
    return response.data;
  };

  public static runPrecheckValidations = async (
    state: UpgradeState
  ): Promise<RequestId> => {
    const response = await Http.post(RUN_PRECHECK_UPGRADE_URL, state);
    return response.data;
  };

  public static runAPUATValidations = async (
    environmentId: string,
    version: string
  ): Promise<ActionResponse> => {
    const response = await Http.post<ActionResponse>(
      APUAT_VALIDATIONS_URL(environmentId, version)
    );
    return response.data;
  };

  public static getAPUATValidations = async (
    environmentId: string,
    version: string
  ): Promise<APUATValidationsResponse> => {
    const response = await Http.get<APUATValidationsResponse>(
      APUAT_VALIDATIONS_URL(environmentId, version)
    );
    return response.data;
  };

  public static async getProductsInterOpMatrix(
    environmentId: string,
    productId: string,
    productVersion: string
  ) {
    const response = await Http.post<ProductsInterOpResponse>(
      GET_PRODUCTS_INTEROP_MATRIX,
      {
        environmentId,
        productId,
        productVersion
      }
    );
    return response.data;
  }

  public static getTargetVersions = async (): Promise<
    TargetVersionsResponse
  > => {
    if (UpgradeWorkflowAPI.targetVersions) {
      return UpgradeWorkflowAPI.targetVersions;
    }
    const response = await Http.get<TargetVersionsResponse>(
      GET_TARGET_VERSIONS_URL
    );
    UpgradeWorkflowAPI.targetVersions = response.data;
    return response.data;
  };

  public static generateUpgradePlan = async (
    data: UpgradePlanInput[]
  ): Promise<UpgradePlan> => {
    const response = await Http.post<UpgradePlan>(FIND_UPGRADE_PATH_URL, data);
    return response.data;
  };

  public static retrieveVraUpgradePlan = async (
    environmentId: string
  ): Promise<string> => {
    const response = await Http.get(
      RETRIEVE_VRA_UPGRADE_PLAN_URL(environmentId)
    );
    return response.data.requestId;
  };
}

function getTargetVidmVersions(version: string | null): string[] {
  return typeof version === 'string'
    ? VIDM_FIXED_UPGRADE_TARGET_VERSIONS[version] || []
    : [];
}

function getTargetVrniVersions(version: string | null): string[] {
  return typeof version === 'string'
    ? VRNI_FIXED_UPGRADE_TARGET_VERSIONS[version] || []
    : [];
}
